import React, { useState } from "react";
import { Button } from "primereact/button";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { checkPermissions } from "../../../../redux/permissions/permissionsUtils";
import { validateIVRConfiguration } from "../../../../redux/api/apiIVRConfiguration";
import type { IvrSchemePlaybackState } from "./IVRSchemeBuilder";

type IVRSchemeNavProps = {
    flowId: number;
    onPlayScheme?: () => Promise<boolean>;
    onPlaySchemeFromSelected?: () => Promise<boolean>;
    onPausePlayback?: () => void;
    onResumePlayback?: () => void;
    onStopPlayback?: () => void;
    playbackState?: IvrSchemePlaybackState;
    canPlayFromSelectedNode?: boolean;
};

const IVRSchemeNav = ({
    flowId,
    onPlayScheme,
    onPlaySchemeFromSelected,
    onPausePlayback,
    onResumePlayback,
    onStopPlayback,
    playbackState = { active: false, paused: false },
    canPlayFromSelectedNode = false,
}: IVRSchemeNavProps) => {
    const dispatch = useAppDispatch();
    const [validateLoading, setValidateLoading] = useState(false);
    const [playAllLoading, setPlayAllLoading] = useState(false);
    const [playFromLoading, setPlayFromLoading] = useState(false);
    const jwtToken = useAppSelector((state) => state.UsersReducer.userData.jwtToken);
    const rolesVision = useAppSelector(
        (state) => state.RolesReducer.rolesVision
    ) as unknown as Record<string, string[]>;
    const ivrConfigurationPermissions = checkPermissions(rolesVision, "IVRConfig");
    const {
        visible: hasIVRConfigurationVisiblePermission,
    } = ivrConfigurationPermissions;
    const token = jwtToken?.jwtToken;
    const playbackActive = playbackState.active;
    const playbackPaused = playbackState.paused;

    const handleValidate = () => {
        if (!token) {
            return;
        }
        setValidateLoading(true);
        void (dispatch(validateIVRConfiguration(flowId, token)) as unknown as Promise<unknown>).finally(
            () => {
                setValidateLoading(false);
            }
        );
    };

    const handlePlay = () => {
        if (!onPlayScheme) {
            return;
        }
        setPlayAllLoading(true);
        void onPlayScheme().finally(() => {
            setPlayAllLoading(false);
        });
    };

    const handlePlayFromSelected = () => {
        if (!onPlaySchemeFromSelected) {
            return;
        }
        setPlayFromLoading(true);
        void onPlaySchemeFromSelected().finally(() => {
            setPlayFromLoading(false);
        });
    };

    const handleTogglePause = () => {
        if (playbackPaused) {
            onResumePlayback?.();
            return;
        }
        onPausePlayback?.();
    };

    if (!hasIVRConfigurationVisiblePermission) {
        return null;
    }

    return (
        <div className="table-buttons">
            <div className="text-normal-weight">
                <Button
                    type="button"
                    label="Перевірити схему"
                    icon="pi pi-check-circle"
                    className="mb-2 mr-2"
                    loading={validateLoading}
                    disabled={!token || playAllLoading || playFromLoading}
                    onClick={handleValidate}
                />
                <Button
                    type="button"
                    label="Відтворити схему"
                    icon="pi pi-play"
                    className="mb-2 mr-2 p-button-outlined"
                    loading={playAllLoading && !playbackPaused}
                    disabled={
                        !onPlayScheme ||
                        validateLoading ||
                        playbackActive ||
                        playFromLoading
                    }
                    onClick={handlePlay}
                />
                {canPlayFromSelectedNode && (
                    <Button
                        type="button"
                        label="Відтворити від елемента"
                        icon="pi pi-step-forward"
                        className="mb-2 mr-2 p-button-outlined p-button-success"
                        loading={playFromLoading && !playbackPaused}
                        disabled={
                            !onPlaySchemeFromSelected ||
                            validateLoading ||
                            playbackActive ||
                            playAllLoading
                        }
                        onClick={handlePlayFromSelected}
                    />
                )}
                <Button
                    type="button"
                    label={playbackPaused ? "Продовжити" : "Призупинити"}
                    icon={playbackPaused ? "pi pi-play" : "pi pi-pause"}
                    className="mb-2 mr-2 p-button-outlined p-button-secondary"
                    disabled={!playbackActive}
                    onClick={handleTogglePause}
                />
                <Button
                    type="button"
                    label="Зупинити"
                    icon="pi pi-stop"
                    className="mb-2 mr-2 p-button-outlined p-button-danger"
                    disabled={!playbackActive}
                    onClick={() => {
                        onStopPlayback?.();
                        setPlayAllLoading(false);
                        setPlayFromLoading(false);
                    }}
                />
            </div>
        </div>
    );
};

export default IVRSchemeNav;
